import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useHotelDashboard } from '@/hooks/useHotelDashboard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import { BarChart3, Plug, Trash2 } from 'lucide-react'
import CompSetCard from '@/components/hotel/CompSetCard'

export default function CompSet() {
  const { hotelId, hotel } = useHotelDashboard()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [roomCount, setRoomCount] = useState('')
  const [adding, setAdding] = useState(false)

  const { data: comps = [], isLoading } = useQuery({
    queryKey: ['comp_properties', hotelId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('comp_properties')
        .select('id, name, room_count, star_rating, distance_miles, created_at')
        .eq('hotel_id', hotelId!)
        .order('name')
      if (error) throw error
      return data
    },
    enabled: !!hotelId,
  })

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    if (!hotelId || !name.trim()) return
    if (comps.length >= 15) {
      toast.error('Maximum 15 comp properties per hotel.')
      return
    }
    setAdding(true)
    try {
      const { error } = await supabase.from('comp_properties').insert({
        hotel_id: hotelId,
        name: name.trim(),
        room_count: roomCount ? parseInt(roomCount, 10) : null,
        source: 'manual',
      })
      if (error) throw error
      toast.success('Comp property added')
      setName('')
      setRoomCount('')
      queryClient.invalidateQueries({ queryKey: ['comp_properties', hotelId] })
      queryClient.invalidateQueries({ queryKey: ['comp_properties_count', hotelId] })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add comp')
    } finally {
      setAdding(false)
    }
  }

  async function handleDelete(id: string) {
    if (!hotelId) return
    const { error } = await supabase.from('comp_properties').delete().eq('id', id)
    if (error) {
      toast.error(error.message)
      return
    }
    toast.success('Removed comp property')
    queryClient.invalidateQueries({ queryKey: ['comp_properties', hotelId] })
    queryClient.invalidateQueries({ queryKey: ['comp_rankings_latest', hotelId] })
  }

  return (
    <div className="min-h-full space-y-8 p-6 text-vesta-navy">
      <div>
        <h1 className="text-2xl font-semibold text-vesta-navy">Comp set</h1>
        <p className="mt-1 text-vesta-navy/80">
          Track {hotel?.name ? `${hotel.name}'s` : 'your'} performance vs nearby properties (ADR & RevPAR).
        </p>
      </div>

      {hotelId && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <CompSetCard hotelId={hotelId} />
          </div>

          <Card className="lg:col-span-2 border border-vesta-navy/10 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-4 w-4 text-vesta-gold" />
                Comp properties ({comps.length}/15)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-3">
                <div className="min-w-[180px] flex-1">
                  <Label htmlFor="comp-name">Property name</Label>
                  <Input
                    id="comp-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nearby hotel name"
                    className="mt-1"
                  />
                </div>
                <div className="w-28">
                  <Label htmlFor="comp-rooms">Rooms</Label>
                  <Input
                    id="comp-rooms"
                    type="number"
                    value={roomCount}
                    onChange={(e) => setRoomCount(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <Button type="submit" disabled={adding || !name.trim()} className="bg-vesta-gold text-vesta-navy">
                  Add comp
                </Button>
              </form>

              {isLoading ? (
                <div className="h-24 animate-pulse rounded-lg bg-vesta-mist/50" />
              ) : comps.length === 0 ? (
                <p className="text-sm text-vesta-navy/75">
                  No comps yet. Add properties above or import via{' '}
                  <Link to="/integrations" className="font-medium underline-offset-2 hover:text-vesta-gold">
                    Integrations
                  </Link>
                  .
                </p>
              ) : (
                <ul className="divide-y divide-vesta-navy/10">
                  {comps.map((c) => (
                    <li key={c.id} className="flex items-center justify-between py-3">
                      <div>
                        <p className="font-medium text-vesta-navy">{c.name}</p>
                        <p className="text-xs text-vesta-navy/65">
                          {c.room_count ? `${c.room_count} rooms` : 'Rooms n/a'}
                          {c.distance_miles != null ? ` · ${c.distance_miles} mi` : ''}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-vesta-navy/50 hover:text-red-700"
                        onClick={() => handleDelete(c.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="rounded-lg border border-vesta-navy/10 bg-vesta-mist/20 p-4 text-sm">
                <p className="flex items-center gap-2 font-medium text-vesta-navy">
                  <Plug className="h-4 w-4" />
                  Import comp metrics
                </p>
                <p className="mt-1 text-vesta-navy/75">
                  Upload comp daily metrics CSV on Integrations (Comp set data). Rankings update automatically.
                </p>
                <Button asChild variant="outline" size="sm" className="mt-3">
                  <Link to="/integrations">Go to Integrations</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
